/**
 * IdentityKey — what makes an entity of this file unique in the case, drawn as the identifier
 * itself: the columns it is built from, in order, the property it is stored in, and a real
 * value from the file. The one element of the review that is never folded away, because it
 * decides which rows — and which files — become the same entity. Pure: an Identity in.
 */

import { ArrowRight, CircleAlert, KeyRound, Rows3 } from 'lucide-react'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/utils/cn'
import type { Identity } from './identity'

const SEGMENT =
  'inline-flex max-w-full items-center rounded-md border px-1.5 py-0.5 font-mono text-xs leading-tight break-all'

function Segment({
  children,
  muted = false,
  title,
}: {
  children: string
  muted?: boolean
  title?: string
}) {
  return (
    <span
      title={title}
      className={cn(
        SEGMENT,
        muted
          ? 'border-dashed border-border text-muted-foreground'
          : 'border-primary/40 bg-primary/5 text-foreground'
      )}
    >
      {children}
    </span>
  )
}

function StoredIn({ property }: { property: string | null }) {
  const { t } = useTranslation()
  const name = property ?? t('tableMapping.identity.noProperty')
  return (
    <span className="inline-flex items-center gap-1 text-xs">
      <span className="sr-only">{t('tableMapping.identity.storedIn', { property: name })}</span>
      <ArrowRight aria-hidden className="size-3 shrink-0 text-muted-foreground" />
      <span
        aria-hidden
        className={cn('font-mono', property ? 'text-primary' : 'text-muted-foreground')}
      >
        {name}
      </span>
    </span>
  )
}

function NumbersOnly({ example }: { example: string }) {
  const { t } = useTranslation()
  return (
    <p className="flex items-start gap-1.5 rounded-md border border-warning-border bg-warning-surface px-2 py-1.5 text-xs text-warning-foreground">
      <CircleAlert aria-hidden className="mt-px size-3.5 shrink-0" />
      <span>
        <span className="font-medium">
          {t('tableMapping.identity.numbersOnly.title', { example })}
        </span>{' '}
        {t('tableMapping.identity.numbersOnly.body')}
      </span>
    </p>
  )
}

function ColumnsKey({ identity }: { identity: Extract<Identity, { kind: 'columns' }> }) {
  const { t } = useTranslation()
  // The fixed prefix comes first, the columns follow in the order the identifier joins them.
  const segments = [
    ...(identity.scope ? [{ text: identity.scope, scope: true }] : []),
    ...identity.columns.map(text => ({ text, scope: false })),
  ]
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex min-w-0 flex-wrap items-center gap-1">
        <KeyRound aria-hidden className="size-3.5 shrink-0 text-primary" />
        {segments.map((segment, index) => (
          <Fragment key={`${segment.scope}:${segment.text}`}>
            {index > 0 && (
              <span aria-hidden className="text-xs text-muted-foreground">
                +
              </span>
            )}
            <Segment
              muted={segment.scope}
              title={segment.scope ? t('tableMapping.identity.scope') : undefined}
            >
              {segment.text}
            </Segment>
          </Fragment>
        ))}
        <StoredIn property={identity.property} />
      </div>
      {(identity.rule || identity.examples.length > 0) && (
        <p className="min-w-0 text-xs text-muted-foreground">
          {identity.rule && t(`tableMapping.identity.rule.${identity.rule}`)}
          {identity.rule && identity.examples.length > 0 && ' '}
          {identity.examples.length > 0 && (
            <>
              {t('tableMapping.identity.example')}{' '}
              <span className="font-mono break-words text-foreground">
                {identity.examples.slice(0, 2).join(', ')}
              </span>
            </>
          )}
        </p>
      )}
      {identity.numbersOnly && <NumbersOnly example={identity.examples[0]} />}
    </div>
  )
}

function ConstantKey({ identity }: { identity: Extract<Identity, { kind: 'constant' }> }) {
  const { t } = useTranslation()
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex min-w-0 flex-wrap items-center gap-1">
        <KeyRound aria-hidden className="size-3.5 shrink-0 text-primary" />
        {identity.value ? (
          <Segment>{identity.value}</Segment>
        ) : (
          <span className="text-xs text-destructive">
            {t('tableMapping.identity.constantMissing')}
          </span>
        )}
        <StoredIn property={identity.property} />
      </div>
      <p className="text-xs text-muted-foreground">{t('tableMapping.identity.constant')}</p>
    </div>
  )
}

function SeparateKey({ kind }: { kind: 'perRow' | 'separateRows' | 'perFile' }) {
  const { t } = useTranslation()
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <p className="flex items-center gap-1.5 text-xs text-foreground">
        <Rows3 aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
        {t(`tableMapping.identity.${kind}`)}
      </p>
      <p className="text-xs text-muted-foreground">{t(`tableMapping.identity.${kind}Hint`)}</p>
    </div>
  )
}

function MissingKey({ reason }: { reason?: 'constant' }) {
  const { t } = useTranslation()
  const message = reason === 'constant' ? 'constantMissing' : 'missing'
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <p className="flex items-center gap-1.5 text-xs font-medium text-destructive">
        <CircleAlert aria-hidden className="size-3.5 shrink-0" />
        {t(`tableMapping.identity.${message}`)}
      </p>
      <p className="text-xs text-muted-foreground">{t(`tableMapping.identity.${message}Hint`)}</p>
    </div>
  )
}

function UnusableKey({
  identity,
}: {
  identity: Extract<Identity, { kind: 'invalid' | 'pending' }>
}) {
  const { t } = useTranslation()
  const title = identity.kind === 'invalid' ? 'invalid' : identity.reason
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <CircleAlert aria-hidden className="size-3.5 shrink-0" />
        {t(`tableMapping.identity.${title}`)}
      </p>
      {identity.property && <span className="font-mono text-xs">{identity.property}</span>}
      {identity.kind === 'invalid' && (
        <>
          <p className="text-xs text-muted-foreground">
            {identity.error || t('tableMapping.identity.invalidHint')}
          </p>
          {identity.allowed.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {t('tableMapping.identity.allowed', { properties: identity.allowed.join(', ') })}
            </p>
          )}
        </>
      )}
    </div>
  )
}

export function IdentityKey({ identity }: { identity: Identity }) {
  if (identity.kind === 'columns') return <ColumnsKey identity={identity} />
  if (identity.kind === 'constant') return <ConstantKey identity={identity} />
  if (identity.kind === 'perRow')
    return <SeparateKey kind={identity.mergeDisabled ? 'separateRows' : 'perRow'} />
  if (identity.kind === 'perFile') return <SeparateKey kind="perFile" />
  if (identity.kind === 'invalid' || identity.kind === 'pending')
    return <UnusableKey identity={identity} />
  return <MissingKey reason={identity.reason} />
}
